import { Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'

interface Address {
  street?: string | null
  number?: string | null
  extraAddressLine?: string | null
  postalCode?: string | null
  town?: string | null
  country?: string | null
}

interface CompanyInfoCardProps {
  title: string
  name?: string | null
  phoneNumber?: string | null
  taxId?: string | null
  invoiceEmail?: string | null
  address?: Address | null
  showAddress?: boolean
  showSepaInstruction?: boolean,
  canEdit?: boolean
  onEdit?: () => void
  canDelete?: boolean
  onDelete?: () => void
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-start gap-4 py-2 border-b last:border-0">
      <span className="text-sm text-muted-foreground w-48 shrink-0">{label}</span>
      <span className="text-sm">{value || '—'}</span>
    </div>
  )
}

export function CompanyInfoCard({
  title,
  name,
  phoneNumber,
  taxId,
  invoiceEmail,
  address,
  showAddress = true,
  showSepaInstruction = false,
  canEdit = false,
  onEdit,
  canDelete = false,
  onDelete,
}: CompanyInfoCardProps) {
  const { t } = useTranslation(['company', 'common'])

  return (
    <Card className="flex-1 min-w-[300px]">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{title}</CardTitle>
          <div className="flex items-center gap-1">
            {canEdit && onEdit && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={onEdit}>
                    <Pencil className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('buttons.edit', { ns: 'common' })}</TooltipContent>
              </Tooltip>
            )}
            {canDelete && onDelete && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={onDelete}>
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('buttons.delete', { ns: 'common' })}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {name !== undefined && <InfoRow label={t('infoCard.fields.company')} value={name} />}
        {phoneNumber !== undefined && <InfoRow label={t('infoCard.fields.phoneNumber')} value={phoneNumber} />}
        {taxId !== undefined && <InfoRow label={t('infoCard.fields.taxId')} value={taxId} />}
        {invoiceEmail !== undefined && <InfoRow label={t('infoCard.fields.invoiceEmail')} value={invoiceEmail} />}
        {showAddress && address && (
          <>
            <InfoRow label={t('infoCard.fields.streetNumber')} value={[address.street, address.number].filter(Boolean).join(' ')} />
            <InfoRow label={t('infoCard.fields.extraAddressLine')} value={address.extraAddressLine} />
            <InfoRow label={t('infoCard.fields.postalCode')} value={address.postalCode} />
            <InfoRow label={t('infoCard.fields.town')} value={address.town} />
            <InfoRow label={t('infoCard.fields.country')} value={address.country} />
          </>
        )}
        <div className='flex flex-row mt-5 gap-6 justify-left'>
          {showSepaInstruction && (
            <div className="rounded-md bg-muted/30 border p-3 text-sm text-muted-foreground">
              <p>{t('infoCard.sepaInstruction')}</p>
              <p>
                Link: {' '}
                <a
                  href="https://bonisoft.de/anleitung-sepa-mandat/"
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-primary"
                >
                  {t('infoCard.sepaLinkText')}
                </a>
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
